import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articleTeasers } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function CategoryDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-5 py-3 rounded-full border-2 border-background-50/40 bg-background-50/15 backdrop-blur-sm text-background-50 text-sm font-label font-medium hover:border-background-50/60 hover:bg-background-50/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-menu-line"></i>
        <span>Kategorie wählen</span>
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="absolute top-full mt-2 left-0 bg-background-50 border border-foreground-200 rounded-xl shadow-sm py-2 w-64 max-h-80 overflow-y-auto z-50">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
              onClick={() => setOpen(false)}
            >
              <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
              </div>
              {cat.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

const heroBgImage = 'https://readdy.ai/api/search-image?query=Luxury%20fashion%20flat%20lay%20photography%20with%20designer%20sneakers%20premium%20leather%20handbag%20silk%20scarf%20and%20gold%20watch%20arranged%20on%20cream%20textured%20surface%20soft%20diffused%20natural%20lighting%20editorial%20quality%20clean%20composition%20with%20generous%20negative%20space%20warm%20beige%20and%20sand%20tones%20sophisticated%20high%20end%20boutique%20aesthetic%20modern%20minimalist%20catalog%20style&width=1800&height=1200&seq=hero-bg-v2-2026&orientation=landscape';

const seoSectionImage = 'https://readdy.ai/api/search-image?query=Modern%20laptop%20on%20light%20oak%20desk%20displaying%20elegant%20product%20catalog%20dashboard%20next%20to%20handwritten%20notebook%20artisan%20coffee%20and%20small%20vase%20with%20dried%20flowers%20warm%20morning%20sunlight%20streaming%20through%20sheer%20curtains%20editorial%20photography%20cream%20taupe%20and%20sage%20tones%20sophisticated%20creative%20workspace%20atmosphere%20high%20detail%20harmonious%20composition&width=1200&height=900&seq=seo-section-v2-2026&orientation=landscape';

const ctaBgImage = 'https://readdy.ai/api/search-image?query=Artistic%20overhead%20flat%20lay%20of%20curated%20luxury%20fashion%20pieces%20including%20leather%20accessories%20minimalist%20jewelry%20silk%20scarves%20and%20premium%20sunglasses%20scattered%20on%20textured%20linen%20fabric%20with%20soft%20directional%20window%20light%20organic%20editorial%20composition%20warm%20neutral%20palette%20high%20end%20boutique%20lifestyle%20photography%20sophisticated%20aspirational%20aesthetic&width=1800&height=800&seq=cta-bg-v2-2026&orientation=landscape';

const catIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-shirt-line',
  'T-Shirts': 'ri-price-tag-3-line',
  'Jackets': 'ri-t-shirt-air-line',
  'Pants/Shorts': 'ri-archive-line',
  'Headwear': 'ri-user-star-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-heart-line',
  'Jersey': 'ri-vip-line',
  'Accessories': 'ri-gemini-line',
  'Bags': 'ri-briefcase-line',
  'Belts': 'ri-link-m',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* ============ HERO SECTION ============ */}
      <header className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBgImage}
            alt="OOPBuy Spreadsheet Produktkollektion 2026"
            className="w-full h-full object-cover object-center"
            title="OOPBuy Spreadsheet Produkte"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/35 to-black/15"></div>
        </div>

        <div className="relative w-full px-4 md:px-8 py-20 md:py-28">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-16">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
                <i className="ri-checkbox-circle-fill text-accent-400 text-xs"></i>
                <span className="text-xs text-background-50/90 font-label tracking-wide">Unabhängiger Deutscher Guide</span>
              </div>

              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5">
                OOPBuy Spreadsheet
                <br />
                <span className="text-background-50/80 font-normal italic">Produkte schneller finden</span>
              </h1>

              <p className="text-sm md:text-base text-background-50/75 leading-relaxed max-w-lg mx-auto lg:mx-0 mb-8">
                Mit dem OOPBuy Spreadsheet findest du strukturierte Produktlisten, aktuelle Empfehlungen und hilfreiche Informationen für deine nächste Bestellung.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-3 mb-8 justify-center lg:justify-start">
                <CategoryDropdown />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-sm"
                >
                  OOPBuy Spreadsheet
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3.5 rounded-full border-2 border-background-50/60 text-background-50 text-sm font-label font-semibold hover:bg-background-50 hover:text-primary-700 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Buy It Now
                  <i className="ri-shopping-bag-line ml-2"></i>
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-8 justify-center lg:justify-start">
                {['Deutsche Anleitung', 'Unabhängiger Guide', 'Produktübersichten', 'Sicherheitsinfos'].map((badge) => (
                  <span key={badge} className="flex items-center gap-1.5 text-xs text-background-50/70 font-label">
                    <i className="ri-check-line text-accent-400 text-xs"></i>
                    {badge}
                  </span>
                ))}
              </div>
            </div>
            <div className="hidden lg:block flex-1"></div>
          </div>
        </div>
      </header>

      {/* ============ PRODUCT CATEGORIES WITH IMAGES ============ */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-3">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">Produktkategorien</h2>
              <p className="text-sm text-foreground-500 font-label">Alle Kategorien mit Produktvorschau</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative flex flex-col items-center rounded-xl bg-background-100 border border-foreground-200/60 hover:border-accent-300 hover:shadow-sm transition-all duration-300 cursor-pointer overflow-hidden"
              >
                <div className="w-full aspect-square overflow-hidden bg-background-200">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    title={`OOPBuy Spreadsheet ${cat.name}`}
                  />
                </div>
                <div className="p-3 w-full text-center">
                  <span className="text-xs md:text-sm font-label font-medium text-foreground-700 group-hover:text-foreground-900 whitespace-nowrap">
                    {cat.name}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHAT IS OOPBUY SPREADSHEET ============ */}
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-1/3 flex-shrink-0">
              <img
                src={seoSectionImage}
                alt="OOPBuy Spreadsheet Anleitung und Übersicht"
                className="w-full rounded-2xl object-cover"
                title="OOPBuy Spreadsheet Guide"
                style={{ maxHeight: '400px' }}
              />
            </div>
            <div className="lg:w-2/3">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-label font-medium mb-4">
                Unabhängiger Guide
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                Was ist ein OOPBuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                Ein OOPBuy Spreadsheet ist eine organisierte Sammlung von Produktinformationen, Links und Kategorien.
                Statt stundenlang nach Produkten zu suchen, können Nutzer mit dem OOPBuy Spreadsheet Produkte schneller
                vergleichen und finden. Die strukturierte Übersicht macht es besonders für neue Nutzer einfach,
                den Überblick zu behalten und die besten Produkte zu entdecken.
              </p>
              <div className="overflow-hidden rounded-xl border border-foreground-200/70 bg-background-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-200/50">
                      <th className="text-left px-4 py-3 font-label font-semibold text-foreground-700">Ohne Spreadsheet</th>
                      <th className="text-left px-4 py-3 font-label font-semibold text-foreground-700">Mit OOPBuy Spreadsheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-foreground-200/50">
                      <td className="px-4 py-2.5 text-foreground-500">Lange Suche</td>
                      <td className="px-4 py-2.5 text-foreground-800 font-medium">Schneller Überblick</td>
                    </tr>
                    <tr className="border-t border-foreground-200/50">
                      <td className="px-4 py-2.5 text-foreground-500">Viele Tabs</td>
                      <td className="px-4 py-2.5 text-foreground-800 font-medium">Strukturierte Liste</td>
                    </tr>
                    <tr className="border-t border-foreground-200/50">
                      <td className="px-4 py-2.5 text-foreground-500">Keine Übersicht</td>
                      <td className="px-4 py-2.5 text-foreground-800 font-medium">Kategorien & Filter</td>
                    </tr>
                    <tr className="border-t border-foreground-200/50">
                      <td className="px-4 py-2.5 text-foreground-500">Mehr Aufwand</td>
                      <td className="px-4 py-2.5 text-foreground-800 font-medium">Einfachere Auswahl</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              Warum ein OOPBuy Spreadsheet nutzen?
            </h2>
            <p className="text-sm text-foreground-500 font-label max-w-xl mx-auto">
              Das OOPBuy Spreadsheet bietet zahlreiche Vorteile für effizientes Produktmanagement
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: 'ri-timer-line', title: 'Zeit sparen', desc: 'Mit dem OOPBuy Spreadsheet findest du Produkte deutlich schneller als mit der normalen Suche.' },
              { icon: 'ri-layout-grid-line', title: 'Bessere Übersicht', desc: 'Alle wichtigen Produktinformationen sind gesammelt und übersichtlich im OOPBuy Spreadsheet dargestellt.' },
              { icon: 'ri-scales-3-line', title: 'Einfach vergleichen', desc: 'Produkte und Preise lassen sich mit dem OOPBuy Spreadsheet im Handumdrehen vergleichen.' },
              { icon: 'ri-user-smile-line', title: 'Anfängerfreundlich', desc: 'Das OOPBuy Spreadsheet ist ideal für neue Nutzer — intuitiv und leicht verständlich aufgebaut.' },
            ].map((benefit) => (
              <div key={benefit.title} className="p-6 rounded-xl bg-background-100 border border-foreground-200/60 hover:border-accent-300 transition-all duration-300 group cursor-default">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${benefit.icon} text-lg text-accent-700`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{benefit.title}</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              So funktioniert das OOPBuy Spreadsheet
            </h2>
            <p className="text-sm text-foreground-500 font-label max-w-xl mx-auto">
              In nur fünf einfachen Schritten zum besseren Produkterlebnis
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              { step: '01', title: 'Spreadsheet öffnen', desc: 'Öffne das OOPBuy Spreadsheet auf der Hauptwebsite' },
              { step: '02', title: 'Kategorie wählen', desc: 'Wähle eine der vielen Produktkategorien im OOPBuy Spreadsheet aus' },
              { step: '03', title: 'Produkt vergleichen', desc: 'Vergleiche Produkte, Preise und Bewertungen im OOPBuy Spreadsheet' },
              { step: '04', title: 'Informationen prüfen', desc: 'Prüfe alle relevanten Produktinformationen und Details sorgfältig' },
              { step: '05', title: 'Zur Website wechseln', desc: 'Wechsel zur Hauptwebsite und entdecke weitere OOPBuy Produkte' },
            ].map((item) => (
              <div key={item.step} className="relative p-5 md:p-6 rounded-xl bg-background-50 border border-foreground-200/60 text-center group hover:border-accent-300 transition-all duration-300 cursor-default">
                <span className="text-3xl md:text-4xl font-heading font-bold text-accent-200 group-hover:text-accent-400 transition-colors duration-300">{item.step}</span>
                <h3 className="font-label font-semibold text-foreground-800 text-sm mt-3 mb-1.5">{item.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SAFETY SECTION ============ */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              Ist das OOPBuy Spreadsheet sicher?
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-8 text-center">
              Sicherheit steht beim OOPBuy Spreadsheet an erster Stelle. Unsere Website speichert keine
              Zahlungsinformationen und verarbeitet keine Bestellungen. Das OOPBuy Spreadsheet dient
              ausschließlich als Informationsquelle und Produktübersicht.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'Produktinformationen vor dem Kauf prüfen',
                'Kundenbewertungen sorgfältig vergleichen',
                'Sichere und vertrauenswürdige Zahlungsmethoden verwenden',
                'Datenschutzbestimmungen der Hauptwebsite beachten',
              ].map((tip) => (
                <div key={tip} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-foreground-200/60">
                  <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-accent-700"></i>
                  </div>
                  <span className="text-sm text-foreground-700 font-label">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMPARISON TABLE ============ */}
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              OOPBuy Spreadsheet vs. normale Produktsuche
            </h2>
            <p className="text-sm text-foreground-500 font-label max-w-xl mx-auto">
              Ein direkter Vergleich zeigt die Vorteile des OOPBuy Spreadsheet
            </p>
          </div>
          <div className="max-w-2xl mx-auto overflow-hidden rounded-xl border border-foreground-200/70 bg-background-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-200/50">
                  <th className="text-left px-5 py-3.5 font-label font-semibold text-foreground-700">Funktion</th>
                  <th className="text-center px-5 py-3.5 font-label font-semibold text-foreground-500">Standard Suche</th>
                  <th className="text-center px-5 py-3.5 font-label font-semibold text-primary-700">Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Übersicht', standard: 'Niedrig', spreadsheet: 'Hoch' },
                  { feature: 'Geschwindigkeit', standard: 'Langsam', spreadsheet: 'Schnell' },
                  { feature: 'Kategorien', standard: 'Nein', spreadsheet: 'Ja' },
                  { feature: 'Vergleich', standard: 'Schwierig', spreadsheet: 'Einfach' },
                  { feature: 'Struktur', standard: 'Keine', spreadsheet: 'Organisiert' },
                ].map((row) => (
                  <tr key={row.feature} className="border-t border-foreground-200/50">
                    <td className="px-5 py-3 font-label font-medium text-foreground-800">{row.feature}</td>
                    <td className="px-5 py-3 text-center text-foreground-500">{row.standard}</td>
                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-primary-700 font-label font-medium">
                        <i className="ri-checkbox-circle-fill text-xs text-accent-500"></i>
                        {row.spreadsheet}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============ 9 SEO ARTICLE TEASER CARDS ============ */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              Artikel & Guides zum OOPBuy Spreadsheet
            </h2>
            <p className="text-sm text-foreground-500 font-label max-w-xl mx-auto">
              Hilfreiche SEO-Artikel mit Tipps, Vergleichen und ausführlichen Anleitungen
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articleTeasers.map((article) => (
              <Link
                key={article.id}
                to={article.link}
                className="group flex flex-col rounded-2xl overflow-hidden bg-background-100 border border-foreground-200/60 hover:border-accent-300 hover:shadow-sm transition-all duration-300 cursor-pointer"
              >
                <div className="relative overflow-hidden h-48 md:h-52">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    title={article.title}
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-label font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed mb-4 flex-1 line-clamp-3">
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-foreground-200/50">
                    <span className="flex items-center gap-1.5 text-xs text-foreground-400 font-label">
                      <i className="ri-time-line"></i>
                      {article.readingTime}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-accent-600 font-label font-medium group-hover:gap-2 transition-all">
                      Weiterlesen
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="w-full py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ctaBgImage}
            alt="OOPBuy Spreadsheet Shopping"
            className="w-full h-full object-cover object-center"
            title="OOPBuy Spreadsheet Shopping"
          />
          <div className="absolute inset-0 bg-black/45"></div>
        </div>
        <div className="relative w-full px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
            Bereit für das <span className="italic font-normal text-background-50/80">OOPBuy Spreadsheet</span>?
          </h2>
          <p className="text-sm md:text-base text-background-50/75 font-label max-w-lg mx-auto mb-8">
            Entdecke die volle Produktvielfalt auf der Hauptwebsite und finde die besten Angebote mit dem OOPBuy Spreadsheet.
          </p>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-sm"
          >
            Zur Hauptwebsite
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* ============ TESTIMONIAL ============ */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
              Nutzerbewertungen
            </h2>
            <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-foreground-200/60">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold mb-6">
                <i className="ri-star-fill text-accent-400 text-xs"></i>
                <span>4.8/5</span>
              </div>
              <blockquote className="font-heading text-lg md:text-xl italic text-foreground-700 leading-relaxed mb-6">
                &ldquo;Das OOPBuy Spreadsheet hat mir enorm geholfen, den Überblick über alle Produkte zu behalten.
                Die strukturierte Übersicht spart mir jede Woche viel Zeit bei der Produktsuche.&rdquo;
              </blockquote>
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-foreground-200 flex items-center justify-center">
                  <i className="ri-user-line text-foreground-500"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm font-label font-semibold text-foreground-800">Markus K.</p>
                  <p className="text-xs text-foreground-400 font-label">Zufriedener Nutzer aus Berlin</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Structured Data: Organization + WebSite */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'OOPBuy Spreadsheet',
            url: (typeof import.meta !== 'undefined' && import.meta.env.VITE_SITE_URL) || 'https://oopbuyspread.space',
            logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
            description: 'Unabhängiger deutscher Guide für OOPBuy Spreadsheet. Produktlisten, Anleitungen, Vergleiche und sichere Nutzung einfach erklärt.',
            sameAs: ['https://kaspreadsheet.com'],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'OOPBuy Spreadsheet Deutsch',
            url: (typeof import.meta !== 'undefined' && import.meta.env.VITE_SITE_URL) || 'https://oopbuyspread.space',
            description: 'Entdecke das beste OOPBuy Spreadsheet auf Deutsch. Der unabhängige deutsche Guide für OOPBuy Spreadsheet.',
            inLanguage: 'de',
          }),
        }}
      />
    </div>
  );
}